import type { SearchEntry } from "@/@types/sidebar-search";
import type { RouteTab, SearchableRoute, UserWithPermissions } from "@/@types/tab-navigation";
import { buildAccessContext, hasRouteAccess } from "@/shared/handle-permission";

import { groupNamesMap } from "../group-names-map";
import { canSeeMenuItem, isShownInModule } from "./menu-visibility";

const WILDCARD_SUFFIX: string = "/*";
const DESCRIPTION_SEPARATOR: string = " › ";

const moduleName = (module: string): string => groupNamesMap[module] ?? module;

const routeUrl = (route: SearchableRoute): string => `${route.pathId}${route.path.replace(WILDCARD_SUFFIX, "")}`;

const moduleOpeningTab = (
  tab: RouteTab,
  modules: string[],
  user: UserWithPermissions | null,
): string | undefined =>
  modules.find((module: string) => hasRouteAccess(tab, buildAccessContext(user, module)));

const toTabEntries = (
  route: SearchableRoute,
  tab: RouteTab,
  modules: string[],
  user: UserWithPermissions | null,
): SearchEntry[] => {
  const tabModule: string | undefined = moduleOpeningTab(tab, modules, user);
  if (!tabModule) return [];

  return [
    {
      key: tab.url,
      label: tab.label,
      context: route.id,
      description: `${moduleName(tabModule)}${DESCRIPTION_SEPARATOR}${route.id}`,
      url: tab.url,
      module: tabModule,
      icon: route.icon,
    },
  ];
};

const toSearchEntries = (
  route: SearchableRoute,
  userGroups: string[],
  user: UserWithPermissions | null,
): SearchEntry[] => {
  const modules: string[] = route.groupRequired.filter(
    (module: string) =>
      isShownInModule(route, module, userGroups) && canSeeMenuItem(route, buildAccessContext(user, module)),
  );
  const [module]: string[] = modules;
  if (!module) return [];

  const routeEntry: SearchEntry = {
    key: route.id,
    label: route.id,
    context: route.category,
    description: moduleName(module),
    url: routeUrl(route),
    module,
    icon: route.icon,
  };
  const tabEntries: SearchEntry[] = (route.tabs ?? []).flatMap((tab: RouteTab) =>
    toTabEntries(route, tab, modules, user),
  );

  return [routeEntry, ...tabEntries];
};

export const buildSearchEntries = (
  routes: SearchableRoute[],
  userGroups: string[],
  user: UserWithPermissions | null,
): SearchEntry[] => routes.flatMap((route: SearchableRoute) => toSearchEntries(route, userGroups, user));

export const matchSearchEntries = (entries: SearchEntry[], term: string): SearchEntry[] => {
  const normalizedTerm: string = term.trim().toLowerCase();
  if (!normalizedTerm) return [];

  return entries.filter(
    (entry: SearchEntry) =>
      entry.label.toLowerCase().includes(normalizedTerm) || entry.context.toLowerCase().includes(normalizedTerm),
  );
};
